import { useContext } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'

import { useCallMutation } from '@/common/api/client'
import { apiPutFetcher } from '@/common/api/fetcher'
import { ChangeMyPasswordResponse, ChangeMyPasswordVars, MeApiPath } from '@/common/api/params/me'
import { useFormErrorMessages, useMessageStates } from '@/common/app/message'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import FormCaption from '@/components/view/atoms/form/FormCaption'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import { FieldType, TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import FormRow from '@/components/view/layouts/form/FormRow'
import ModalContent from '@/components/view/layouts/modal/ModalContent'
import { MessageType } from '@/components/view/molecules/bar/MessageBar'
import ModalFooter from '@/components/view/molecules/modal/ModalFooter'

import FormError from '@/components/container/form/widgets/FormError'
import FormMessageList from '@/components/container/form/widgets/FormMessageList'
import FormTextField, { FormTextFieldType } from '@/components/container/form/widgets/FormTextField'
import { ToastMessageContext } from '@/components/container/utils/message/ToastMessageProvider'

import {
  DEFAULT_PASSWORD_SCHEMA,
  PASSWORD_CAPTION,
  PasswordFormSchemaType,
  convertPasswordSchemaToVars,
  passwordNameLabelMapping,
  passwordSchema
} from './passwordSchema'

interface Props {
  hide: () => void
}

const PasswordForm = ({ hide }: Props) => {
  const { addMessage: addToast } = useContext(ToastMessageContext)
  const [messages, setMessages] = useMessageStates()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<PasswordFormSchemaType>({
    resolver: zodResolver(passwordSchema),
    defaultValues: DEFAULT_PASSWORD_SCHEMA,
    mode: 'onChange'
  })

  useFormErrorMessages(errors, setMessages, passwordNameLabelMapping)

  const { trigger } = useCallMutation<ChangeMyPasswordResponse, ChangeMyPasswordVars>(
    MeApiPath.CHANGE_MY_PASSWORD,
    {
      onSuccess: () => {
        addToast({ text: 'パスワードを変更しました', type: MessageType.SUCCESS })
        reset()
        hide()
      },
      onError: (error) => {
        error.json().then((data) => {
          if (data.detail) {
            addToast({ text: data.detail, type: MessageType.CRITICAL })
          } else {
            addToast({ text: 'パスワードの変更に失敗しました', type: MessageType.CRITICAL })
          }
        })
      },
      errorNotShown: true
    },
    apiPutFetcher
  )

  const onSubmit: SubmitHandler<PasswordFormSchemaType> = (data) =>
    trigger(convertPasswordSchemaToVars(data))

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ModalContent>
        <FormMessageList messageStates={messages} setMessageStates={setMessages} />
        <FormRow direction='vertical' gap={2}>
          <FormLabel required id='currentPassword' fixWidth={null}>
            現在のパスワード
          </FormLabel>
          <FormTextField
            register={register('currentPassword')}
            fieldType={FieldType.REQUIRED}
            type={FormTextFieldType.PASSWORD}
            error={!!errors.currentPassword?.message}
            size={TextBoxSize.LARGE}
          />
          <FormError message={errors.currentPassword?.message} />
        </FormRow>
        <FormRow direction='vertical' gap={2}>
          <FormLabel required id='newPassword' fixWidth={null}>
            新しいパスワード
          </FormLabel>
          <FormTextField
            register={register('newPassword')}
            fieldType={FieldType.REQUIRED}
            type={FormTextFieldType.PASSWORD}
            error={!!errors.newPassword?.message}
            size={TextBoxSize.LARGE}
          />
          <FormCaption>{PASSWORD_CAPTION}</FormCaption>
          <FormError message={errors.newPassword?.message} />
        </FormRow>
        <FormRow direction='vertical' gap={2}>
          <FormLabel required id='confirmPassword' fixWidth={null}>
            新しいパスワード（確認）
          </FormLabel>
          <FormTextField
            register={register('confirmPassword')}
            fieldType={FieldType.REQUIRED}
            type={FormTextFieldType.PASSWORD}
            error={!!errors.confirmPassword?.message}
            size={TextBoxSize.LARGE}
          />
          <FormError message={errors.confirmPassword?.message} />
        </FormRow>
      </ModalContent>
      <ModalFooter>
        <Button color={'secondary'} size={'large'} onClick={hide}>
          閉じる
        </Button>
        <Button
          type='submit'
          color={ButtonColor.PRIMARY_BRAND}
          size={'large'}
          loading={isSubmitting}
        >
          パスワードを変更
        </Button>
      </ModalFooter>
    </form>
  )
}

export default PasswordForm
